import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import cogoToast from "cogo-toast";
import { getLeadMagnet, postLeadMagnetResponseForm } from "../api/utils";
import useFetch from "../api/hooks/useFetch";
import CustomSoftwareROICalculatorStyles from "./styles/CustomSoftwareROICalculator";

const CustomSoftwareROICalculator = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();

  const fallbackSlugMap = {
    "/lm/custom-software-roi-calculator": "custom-software-roi-calculator",
  };

  const actualSlug = paramSlug || fallbackSlugMap[location.pathname] || "";

  const searchParams = new URLSearchParams(location.search);
  const utmParams = {
    utm_source: searchParams.get("utm_source") || "direct",
    utm_medium: searchParams.get("utm_medium") || "email",
    utm_campaign: searchParams.get("utm_campaign") || "default",
  };

  const {
    apiData: leadMagnet,
    isLoading: isLeadMagnetLoding,
    error: leadMagnetError,
  } = useFetch(() => getLeadMagnet(actualSlug, utmParams), actualSlug);

  useEffect(() => {
    if (leadMagnetError) {
      cogoToast.error("Failed to load content");
    }
    if (leadMagnet?.status === "error") {
      cogoToast.error("Failed to load content");
    }
  }, [leadMagnetError, leadMagnet]);

  // State for input values
  const [inputs, setInputs] = useState({
    // Off-the-shelf Software Costs (USD)
    baseSubscription: 60,
    userCount: 10,
    perUserCost: 6,
    addOns: 36,
    integrations: 24,
    priceIncrease: 10,

    // Custom Software Development Costs (USD)
    devCost: 18000,
    hosting: 1200,
    maintenance: 2400,

    // Additional Factors
    timeSaved: 20,
    hourlyRate: 6,
  });

  // State for results
  const [results, setResults] = useState({
    showResults: false,
    offShelfTotal: 0,
    customTotal: 0,
    savings: 0,
    savingsPercent: 0,
    roiPercent: 0,
    paybackPeriod: 0,
    timeSavingsValue: 0,
    breakEven: 0,
    offShelfBreakdown: [],
    customBreakdown: "",
    recommendation: "",
  });

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return "US$" + amount.toLocaleString("en-US", { maximumFractionDigits: 0 });
  };

  // Calculate ROI
  const calculateROI = () => {
    // Destructure inputs
    const {
      baseSubscription,
      userCount,
      perUserCost,
      addOns,
      integrations,
      priceIncrease,
      devCost,
      hosting,
      maintenance,
      timeSaved,
      hourlyRate,
    } = inputs;

    // Calculate off-the-shelf costs over 5 years
    let offShelfTotal = 0;
    let yearlyOffShelf = [];
    let monthlyCost =
      baseSubscription + userCount * perUserCost + addOns + integrations;

    for (let year = 1; year <= 5; year++) {
      const yearlyCost = monthlyCost * 12;
      yearlyOffShelf.push(yearlyCost);
      offShelfTotal += yearlyCost;
      monthlyCost = monthlyCost * (1 + priceIncrease / 100);
    }

    // Calculate custom software costs over 5 years
    const customTotal = devCost + (hosting + maintenance) * 5;

    // Calculate time savings value
    const weeklyTimeSavingsValue = timeSaved * hourlyRate;
    const annualTimeSavingsValue = weeklyTimeSavingsValue * 52;
    const totalTimeSavingsValue = annualTimeSavingsValue * 5;

    // Calculate savings
    const savings = offShelfTotal - customTotal;
    const savingsPercent =
      offShelfTotal > 0 ? ((savings / offShelfTotal) * 100).toFixed(1) : 0;

    // Calculate ROI
    const roi =
      customTotal > 0 ? ((savings / customTotal) * 100).toFixed(1) : 0;

    // Calculate payback period (in months)
    const monthlyOffShelf =
      baseSubscription + userCount * perUserCost + addOns + integrations;
    const monthlyCustom = (hosting + maintenance) / 12;
    const monthlySavings = monthlyOffShelf - monthlyCustom;
    const paybackMonths =
      monthlySavings > 0 ? Math.ceil(devCost / monthlySavings) : 0;

    // Calculate break-even
    const breakEvenMonths = paybackMonths;

    // Generate recommendation
    let recommendation = "";
    let recommendationType = "";

    if (savings > 0 && paybackMonths <= 18) {
      recommendationType = "strong";
      recommendation = `Custom software is a <strong>highly recommended investment</strong> for your business! You'll save <strong>${formatCurrency(
        savings
      )} over 5 years</strong> and break even in just <strong>${paybackMonths} months</strong>. Plus, you'll own the software outright with complete control and no vendor lock-in.`;
    } else if (savings > 0 && paybackMonths <= 36) {
      recommendationType = "good";
      recommendation = `Custom software is a <strong>solid long-term investment</strong>. While the payback period is ${paybackMonths} months, you'll save <strong>${formatCurrency(
        savings
      )} over 5 years</strong> and own the software completely. The time savings alone are worth <strong>${formatCurrency(
        totalTimeSavingsValue
      )}</strong>.`;
    } else if (savings > 0) {
      recommendationType = "moderate";
      recommendation = `Custom software offers <strong>significant long-term benefits</strong> despite a longer payback period. You'll save <strong>${formatCurrency(
        savings
      )} over 5 years</strong> while gaining complete customization, no vendor lock-in, and a competitive advantage.`;
    } else {
      recommendationType = "consider";
      recommendation = `While off-the-shelf appears cheaper initially, consider the <strong>strategic advantages of custom software</strong>: complete ownership, perfect process fit, and no subscription dependency. A phased MVP approach could optimize costs.`;
    }

    // Set results
    setResults({
      showResults: true,
      offShelfTotal,
      customTotal,
      savings,
      savingsPercent,
      roiPercent: roi,
      paybackPeriod: paybackMonths,
      timeSavingsValue: totalTimeSavingsValue,
      breakEven: breakEvenMonths,
      offShelfBreakdown: yearlyOffShelf,
      customBreakdown: `
        <strong>Development:</strong> ${formatCurrency(devCost)}<br>
        <strong>Hosting (5yr):</strong> ${formatCurrency(hosting * 5)}<br>
        <strong>Maintenance (5yr):</strong> ${formatCurrency(
          maintenance * 5
        )}<br>
        <em>You own the software outright!</em>
      `,
      recommendation,
      recommendationType,
    });

    // Scroll to results
    setTimeout(() => {
      const resultsElement = document.getElementById("roi-results");
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  // Reset form
  const resetCalculator = () => {
    setInputs({
      baseSubscription: 60,
      userCount: 10,
      perUserCost: 6,
      addOns: 36,
      integrations: 24,
      priceIncrease: 10,
      devCost: 18000,
      hosting: 1200,
      maintenance: 2400,
      timeSaved: 20,
      hourlyRate: 6,
    });
    setResults({
      showResults: false,
      offShelfTotal: 0,
      customTotal: 0,
      savings: 0,
      savingsPercent: 0,
      roiPercent: 0,
      paybackPeriod: 0,
      timeSavingsValue: 0,
      breakEven: 0,
      offShelfBreakdown: [],
      customBreakdown: "",
      recommendation: "",
    });
  };

  return (
    <div className="roi-calculator-page">
      {/* Hero Header */}
      <section className="calculator-hero" style={{ paddingTop: 120 }}>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">📊</span>
              <span className="badge-text">ROI CALCULATOR</span>
            </div>
            <h1 className="hero-title">
              Custom Software <span className="highlight">ROI Calculator</span>
            </h1>
            <p className="hero-subtitle">
              Compare 5-year costs: Off-the-shelf vs Custom Development. See
              exactly how much you can save with a custom solution.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-value">68%</div>
                <div className="stat-label">Average Efficiency Gain</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">8-18</div>
                <div className="stat-label">Months to ROI</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">95%</div>
                <div className="stat-label">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Calculator */}
      <div className="container main-calculator">
        {/* Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-header">
            <h2>Enter Your Business Details</h2>
            <p>Adjust the sliders and inputs to match your business scenario</p>
          </div>

          {/* Input Sections */}
          <div className="input-sections" style={{ paddingTop: 40 }}>
            {/* Section 1: Off-the-shelf Costs */}
            <section className="input-section">
              <div className="section-header">
                <div className="section-icon">💸</div>
                <div className="section-header-content">
                  <h3>Off-the-Shelf Software Costs</h3>
                  <p>Monthly subscription costs and additional expenses</p>
                </div>
              </div>

              <div className="input-grid">
                <div className="input-group">
                  <label>
                    <span className="label-text">
                      Base Monthly Subscription
                    </span>
                    <span className="label-value">
                      ${inputs.baseSubscription}/month
                    </span>
                  </label>
                  <input
                    type="range"
                    name="baseSubscription"
                    min="10"
                    max="500"
                    step="10"
                    value={inputs.baseSubscription}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>$10</span>
                    <span>$250</span>
                    <span>$500</span>
                  </div>
                </div>

                <div className="input-group">
                  <label>
                    <span className="label-text">Number of Users</span>
                    <span className="label-value">
                      {inputs.userCount} users
                    </span>
                  </label>
                  <input
                    type="range"
                    name="userCount"
                    min="1"
                    max="100"
                    value={inputs.userCount}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>1</span>
                    <span>50</span>
                    <span>100</span>
                  </div>
                </div>

                <div className="input-group">
                  <label>
                    <span className="label-text">Cost Per User/Month</span>
                    <span className="label-value">
                      ${inputs.perUserCost}/user
                    </span>
                  </label>
                  <input
                    type="range"
                    name="perUserCost"
                    min="1"
                    max="50"
                    step="1"
                    value={inputs.perUserCost}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>$1</span>
                    <span>$25</span>
                    <span>$50</span>
                  </div>
                </div>

                <div className="input-group">
                  <label>
                    <span className="label-text">Add-ons & Features</span>
                    <span className="label-value">${inputs.addOns}/month</span>
                  </label>
                  <input
                    type="range"
                    name="addOns"
                    min="0"
                    max="200"
                    step="10"
                    value={inputs.addOns}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>$0</span>
                    <span>$100</span>
                    <span>$200</span>
                  </div>
                </div>

                <div className="input-group">
                  <label>
                    <span className="label-text">Integration Tools</span>
                    <span className="label-value">
                      ${inputs.integrations}/month
                    </span>
                  </label>
                  <input
                    type="range"
                    name="integrations"
                    min="0"
                    max="100"
                    step="10"
                    value={inputs.integrations}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>$0</span>
                    <span>$50</span>
                    <span>$100</span>
                  </div>
                </div>

                <div className="input-group">
                  <label>
                    <span className="label-text">Annual Price Increase</span>
                    <span className="label-value">{inputs.priceIncrease}%</span>
                  </label>
                  <input
                    type="range"
                    name="priceIncrease"
                    min="0"
                    max="30"
                    step="1"
                    value={inputs.priceIncrease}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>0%</span>
                    <span>15%</span>
                    <span>30%</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2: Custom Development Costs */}
            <section className="input-section">
              <div className="section-header">
                <div className="section-icon">💻</div>
                <div className="section-header-content">
                  <h3>Custom Development Costs</h3>
                  <p>One-time investment with annual maintenance</p>
                </div>
              </div>

              <div className="input-grid">
                <div className="input-group">
                  <label>
                    <span className="label-text">Development Cost</span>
                    <span className="label-value">
                      {formatCurrency(inputs.devCost)}
                    </span>
                  </label>
                  <input
                    type="range"
                    name="devCost"
                    min="5000"
                    max="100000"
                    step="1000"
                    value={inputs.devCost}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>$5K</span>
                    <span>$50K</span>
                    <span>$100K</span>
                  </div>
                </div>

                <div className="input-group">
                  <label>
                    <span className="label-text">Annual Hosting</span>
                    <span className="label-value">
                      {formatCurrency(inputs.hosting)}/year
                    </span>
                  </label>
                  <input
                    type="range"
                    name="hosting"
                    min="200"
                    max="5000"
                    step="100"
                    value={inputs.hosting}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>$200</span>
                    <span>$2.5K</span>
                    <span>$5K</span>
                  </div>
                </div>

                <div className="input-group">
                  <label>
                    <span className="label-text">Annual Maintenance</span>
                    <span className="label-value">
                      {formatCurrency(inputs.maintenance)}/year
                    </span>
                  </label>
                  <input
                    type="range"
                    name="maintenance"
                    min="500"
                    max="10000"
                    step="100"
                    value={inputs.maintenance}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>$500</span>
                    <span>$5K</span>
                    <span>$10K</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 3: Additional Factors */}
            <section className="input-section">
              <div className="section-header">
                <div className="section-icon">⏱️</div>
                <div className="section-header-content">
                  <h3>Efficiency Gains</h3>
                  <p>Time savings and productivity improvements</p>
                </div>
              </div>

              <div className="input-grid">
                <div className="input-group">
                  <label>
                    <span className="label-text">Time Saved Per Week</span>
                    <span className="label-value">
                      {inputs.timeSaved} hours
                    </span>
                  </label>
                  <input
                    type="range"
                    name="timeSaved"
                    min="0"
                    max="40"
                    step="1"
                    value={inputs.timeSaved}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>0h</span>
                    <span>20h</span>
                    <span>40h</span>
                  </div>
                </div>

                <div className="input-group">
                  <label>
                    <span className="label-text">Average Hourly Rate</span>
                    <span className="label-value">
                      ${inputs.hourlyRate}/hour
                    </span>
                  </label>
                  <input
                    type="range"
                    name="hourlyRate"
                    min="5"
                    max="100"
                    step="5"
                    value={inputs.hourlyRate}
                    onChange={handleInputChange}
                    className="range-slider"
                  />
                  <div className="slider-ticks">
                    <span>$5</span>
                    <span>$50</span>
                    <span>$100</span>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Calculate Button */}
          <div className="calculate-section">
            <button className="calculate-btn" onClick={calculateROI}>
              <span className="btn-icon">📊</span>
              <span className="btn-text">Calculate Your ROI</span>
              <span className="btn-arrow">→</span>
            </button>
            <p className="calculate-note">
              Based on 5-year projections. All calculations are estimates.
            </p>
          </div>
        </div>

        {/* Results Section */}
        {results.showResults && (
          <div className="results-section" id="roi-results">
            {/* Results Header */}
            <div className="results-header">
              <h2>Your Custom Software ROI Analysis</h2>
              <p>Based on your inputs, here's the 5-year projection</p>
            </div>

            {/* Key Comparison */}
            <div className="comparison-grid">
              <div className="comparison-card off-shelf">
                <div className="card-header">
                  <div className="card-icon">📦</div>
                  <h3>Off-the-Shelf</h3>
                </div>
                <div className="card-body">
                  <div className="total-cost">
                    <span className="cost-label">5-Year Total Cost</span>
                    <span className="cost-value">
                      {formatCurrency(results.offShelfTotal)}
                    </span>
                  </div>
                  <div className="breakdown">
                    <h4>Year-by-Year Breakdown:</h4>
                    {results.offShelfBreakdown.map((cost, index) => (
                      <div key={index} className="breakdown-item">
                        <span className="breakdown-label">
                          Year {index + 1}:
                        </span>
                        <span className="breakdown-value">
                          {formatCurrency(cost)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="card-note">
                    <span className="note-icon">⚠️</span>
                    <span className="note-text">
                      Annual price increases included
                    </span>
                  </div>
                </div>
              </div>

              <div className="comparison-card custom">
                <div className="card-header">
                  <div className="card-icon">✨</div>
                  <h3>Custom Software</h3>
                </div>
                <div className="card-body">
                  <div className="total-cost">
                    <span className="cost-label">5-Year Total Cost</span>
                    <span className="cost-value saving">
                      {formatCurrency(results.customTotal)}
                    </span>
                  </div>
                  <div className="breakdown">
                    <h4>Cost Breakdown:</h4>
                    <div className="breakdown-item">
                      <span className="breakdown-label">Development:</span>
                      <span className="breakdown-value">
                        {formatCurrency(inputs.devCost)}
                      </span>
                    </div>
                    <div className="breakdown-item">
                      <span className="breakdown-label">Hosting (5yr):</span>
                      <span className="breakdown-value">
                        {formatCurrency(inputs.hosting * 5)}
                      </span>
                    </div>
                    <div className="breakdown-item">
                      <span className="breakdown-label">
                        Maintenance (5yr):
                      </span>
                      <span className="breakdown-value">
                        {formatCurrency(inputs.maintenance * 5)}
                      </span>
                    </div>
                  </div>
                  <div className="card-note positive">
                    <span className="note-icon">🎯</span>
                    <span className="note-text">
                      You own the software outright!
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Savings Highlight */}
            <div className="savings-highlight">
              <div className="savings-content">
                <div className="savings-icon">💰</div>
                <div className="savings-text">
                  <h3>Your 5-Year Savings</h3>
                  <p>
                    That's {results.savingsPercent}% less than off-the-shelf
                  </p>
                </div>
                <div className="savings-amount">
                  {formatCurrency(results.savings)}
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">📈</div>
                <div className="metric-content">
                  <div className="metric-value">{results.roiPercent}%</div>
                  <div className="metric-label">Return on Investment</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">⏳</div>
                <div className="metric-content">
                  <div className="metric-value">{results.paybackPeriod}</div>
                  <div className="metric-label">Months to Payback</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">💡</div>
                <div className="metric-content">
                  <div className="metric-value">
                    {formatCurrency(results.timeSavingsValue)}
                  </div>
                  <div className="metric-label">Time Savings Value</div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">🎯</div>
                <div className="metric-content">
                  <div className="metric-value">Month {results.breakEven}</div>
                  <div className="metric-label">Break-Even Point</div>
                </div>
              </div>
            </div>

            {/* Recommendation */}
            <div
              className={`recommendation-card ${results.recommendationType}`}
            >
              <div className="recommendation-header">
                <div className="recommendation-icon">💡</div>
                <h3>Our Recommendation</h3>
              </div>
              <div className="recommendation-body">
                <div
                  className="recommendation-text"
                  dangerouslySetInnerHTML={{ __html: results.recommendation }}
                />
                <div className="recommendation-cta">
                  <button
                    className="cta-primary"
                    onClick={() =>
                      window.open(
                        "https://topmate.io/pluralcode/1677658",
                        "_blank"
                      )
                    }
                  >
                    <span>Schedule Free Consultation</span>
                    <span>→</span>
                  </button>
                  <button className="cta-secondary" onClick={resetCalculator}>
                    <span>Recalculate</span>
                    <span>↻</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="summary-section">
              <h3>Key Takeaways</h3>
              <div className="summary-grid">
                <div className="summary-item">
                  <div className="summary-icon">🏆</div>
                  <div className="summary-content">
                    <h4>Complete Ownership</h4>
                    <p>No vendor lock-in, full control over your software</p>
                  </div>
                </div>
                <div className="summary-item">
                  <div className="summary-icon">⚡</div>
                  <div className="summary-content">
                    <h4>Perfect Fit</h4>
                    <p>Tailored exactly to your business processes</p>
                  </div>
                </div>
                <div className="summary-item">
                  <div className="summary-icon">📊</div>
                  <div className="summary-content">
                    <h4>Scalable Solution</h4>
                    <p>Grows with your business, no subscription limits</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <CustomSoftwareROICalculatorStyles />
    </div>
  );
};

export default CustomSoftwareROICalculator;
